import { homeStatusLabel } from './statusLabel'
import { bedrockServiceStatus } from './bedrockStatus'
import { providerAvailabilityLabel } from '../../classes/ProviderAvailability'

// Public service-health feeds only. Subscription credentials never go to these URLs.
// `name` is the fixed triangle heading: at most 10 characters, never a catalog/feed display name.
const providers = {
  // Token-pack backend/region verified against Cloud Run on 2026-09-16. Keep aligned
  // with scripts/deploy-cloud-run.sh if IntelliKey's managed inference service changes.
  intellikey: { name: 'IntelliKey', url: 'https://status.aws.amazon.com/rss/bedrock-us-east-2.rss', format: 'rss' },
  openai: { name: 'OpenAI', url: 'https://status.openai.com/api/v2/status.json' },
  'github-copilot': {
    name: 'Copilot',
    url: 'https://www.githubstatus.com/api/v2/summary.json',
    components: ['Copilot', 'Copilot AI Model Providers']
  },
  kimi: { name: 'Kimi', url: 'https://status.moonshot.cn/api/v2/status.json' },
  cursor: { name: 'Cursor', url: 'https://status.cursor.com/api/v2/status.json' },
  minimax: {
    name: 'MiniMax',
    url: 'https://status.minimax.io/api/v2/summary.json',
    components: ['Large Language Models (LLM)']
  },
  grok: { name: 'Grok' },
  openrouter: { name: 'OpenRouter' },
  mistral: { name: 'Mistral' },
  meta: { name: 'Meta' },
  'aws-bedrock': { name: 'Bedrock' },
  'aws-anthropic': { name: 'Anthropic' }
}

const componentStates = {
  operational: { label: 'Online', indicator: 'none', severity: 0 },
  under_maintenance: { label: 'Maintenance', indicator: 'maintenance', severity: 1 },
  degraded_performance: { label: 'Degraded', indicator: 'minor', severity: 2 },
  partial_outage: { label: 'Limited', indicator: 'major', severity: 3 },
  major_outage: { label: 'Offline', indicator: 'critical', severity: 4 }
}

export const emptySubscriptionStatus = { provider: '', name: 'IntelliKey', label: '', indicator: 'none' }

export const selectedSubscription = snapshot => {
  const provider = snapshot?.selectedProvider
  if (/^device-mac:[a-f0-9]{64}$/.test(provider || '')) {
    const connection = snapshot.connections?.find(item => item.id === provider)
    const device = snapshot.catalog?.find(item => item.id === provider)
    const connected = connection?.online === true
    return { provider, name: 'Your Mac', connected,
      ...(device?.local && !connected ? { label: device.starting ? 'Starting' : 'Stopped' } : {}) }
  }
  if (!Object.prototype.hasOwnProperty.call(providers, provider)) return null
  const connections = Array.isArray(snapshot.connections) ? snapshot.connections : snapshot.providers
  const connection = Array.isArray(connections) && connections.find(item => item.id === provider)
  // Missing connection metadata isn't evidence of a disconnected account.
  if (!connection || typeof connection.connected !== 'boolean') return null
  if (!connection.connected) {
    if (provider === 'aws-bedrock' || provider === 'aws-anthropic') {
      return { provider, name: providers[provider].name, connected: false }
    }
    return { provider: 'intellikey', name: 'IntelliKey', connected: true }
  }
  return { provider, name: providers[provider].name, connected: connection.connected,
    ...(connection.availability ? { availability: connection.availability } : {}) }
}

export const connectionStatus = selection => {
  if (!selection) return { ...emptySubscriptionStatus, label: 'Disconnected' }
  return {
    provider: selection.provider, name: selection.name,
    label: providerAvailabilityLabel(selection.availability) || selection.label || (selection.connected ? 'Connected' : 'Disconnected'),
    indicator: providerAvailabilityLabel(selection.availability) ? 'major' : 'none'
  }
}

export const providerServiceStatus = (provider, data) => {
  const configuration = providers[provider]
  if (!configuration?.url) return null
  if (provider === 'intellikey') return bedrockServiceStatus(data)
  if (configuration.components) {
    // An outage in GitHub Actions or MiniMax video isn't an outage in the selected AI service.
    const components = Array.isArray(data?.components) ? data.components : []
    const states = configuration.components.map(name => {
      const component = components.find(item => item.name === name)
      return componentStates[component?.status]
    })
    if (states.some(state => !state)) return null
    const worst = states.reduce((left, right) => left.severity >= right.severity ? left : right)
    return { label: worst.label, indicator: worst.indicator }
  }
  const label = homeStatusLabel(data?.status)
  if (!label) return null
  const indicators = { Online: 'none', Degraded: 'minor', Limited: 'major', Offline: 'critical', Maintenance: 'maintenance' }
  // Keep the existing upstream-indicator color rules, independently of the shortened wording.
  const indicator = Object.values(indicators).includes(data.status.indicator)
    ? data.status.indicator
    : indicators[label]
  return { label, indicator }
}

const fetchServiceStatus = async (url, signal, format) => {
  const response = await fetch(url, { signal, credentials: 'omit', referrerPolicy: 'no-referrer' })
  if (!response.ok) throw new Error('Service status unavailable')
  return format === 'rss' ? response.text() : response.json()
}

// Owned by Home: polls one selected provider, and retires requests on account changes/unmount.
export class HomeSubscriptionStatus {
  constructor ({ getSubscriptions, onChange, fetchStatus = fetchServiceStatus }) {
    this.getSubscriptions = getSubscriptions
    this.onChange = onChange
    this.fetchStatus = fetchStatus
    this.generation = 0
    this.account = null
    this.selection = null
    this.stopped = false
  }

  setAccount = uid => {
    const account = uid || null
    if (this.stopped || account === this.account) return
    this.cancelRequest()
    clearInterval(this.interval)
    this.account = account
    this.selection = null
    this.onChange(emptySubscriptionStatus)
    if (account) {
      this.interval = setInterval(this.refresh, 30000)
      this.refresh()
    }
  }

  cancelRequest = () => {
    this.generation += 1
    if (this.controller) this.controller.abort()
    this.controller = null
    clearTimeout(this.timeout)
  }

  refresh = async () => {
    if (this.stopped || !this.account) return
    this.cancelRequest()
    const generation = this.generation
    const isCurrent = () => !this.stopped && generation === this.generation
    let selection
    try {
      // Existing metadata source respects the native Private setting; it does not fetch health feeds.
      selection = selectedSubscription(await this.getSubscriptions())
    } catch {
      if (isCurrent()) {
        this.selection = null
        this.onChange(connectionStatus(null))
      }
      return
    }
    if (!isCurrent()) return
    const changed = selection?.provider !== this.selection?.provider || selection?.connected !== this.selection?.connected
    this.selection = selection
    const fallback = connectionStatus(selection)
    if (providerAvailabilityLabel(selection?.availability)) {
      this.onChange(fallback)
      return
    }
    if (changed || !selection) this.onChange(fallback)
    const url = selection?.connected && providers[selection.provider]?.url
    if (!url) {
      this.onChange(fallback)
      return
    }
    const controller = new AbortController()
    this.controller = controller
    // Publish the connection fallback on timeout even if a transport ignores cancellation.
    this.timeout = setTimeout(() => {
      if (!isCurrent()) return
      this.cancelRequest()
      this.onChange(fallback)
    }, 8000)
    try {
      const data = await this.fetchStatus(url, controller.signal, providers[selection.provider].format)
      if (!isCurrent()) return
      const service = providerServiceStatus(selection.provider, data)
      this.onChange(service ? { ...fallback, ...service } : fallback)
    } catch {
      if (isCurrent()) this.onChange(fallback)
    } finally {
      if (isCurrent()) {
        clearTimeout(this.timeout)
        this.controller = null
      }
    }
  }

  stop = () => {
    this.stopped = true
    this.cancelRequest()
    clearInterval(this.interval)
  }
}
